import { useCallback, useMemo } from "react";
import {
  PAYMENT_TERMINALS_QUERY,
  usePaymentTerminalCreateMutation,
} from "@ticketingplatform/api/dist/paymentTerminal";
import { Form, message } from "antd";
import useApolloErrorFields from "hooks/useApolloErrorFields";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { ROUTE_PATHS } from "const/paths";
import usePrompt from "hooks/usePrompt";

import { PaymentTerminalFormValues } from "../PaymentTerminalForm/types";

import { UsePaymentTerminal, UsePaymentTerminalAddReturnType } from "./types";
import { initialValues } from "./consts";

export const usePaymentTerminalAdd: UsePaymentTerminal = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [paymentTerminalCreateMutation, { loading, error }] =
    usePaymentTerminalCreateMutation({
      evictQueries: [PAYMENT_TERMINALS_QUERY],
    });

  const fields = useApolloErrorFields(error);

  usePrompt(loading);

  const onFinish = useCallback(
    async ({ input: { venue, ...input } }: PaymentTerminalFormValues) => {
      try {
        await paymentTerminalCreateMutation({
          variables: {
            input: {
              ...input,
              venueId: venue?.id,
            },
          },
        });

        message.success(
          intl.formatMessage({
            defaultMessage: "Payment Terminal has been added!",
          })
        );

        navigate(ROUTE_PATHS.PaymentTerminalsList, {
          state: {
            skipBlock: true,
          },
        });
      } catch {
        message.error(
          intl.formatMessage({
            defaultMessage: "Error. Cannot add Payment Terminal.",
          })
        );
      }
    },
    [intl, navigate, paymentTerminalCreateMutation]
  );

  return useMemo<UsePaymentTerminalAddReturnType>(
    () => ({
      form: {
        initialValues,
        fields,
        loading,
        form,
        onFinish,
      },
    }),
    [fields, form, loading, onFinish]
  );
};
